import React from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";

const AnimatedTextSection = ({
  text,
  highlightWords = [],
  bgColor = "#1e1e21",
  textColor = "#ffffff",
  highlightColor = "#ea3d95",
}) => {
  const ref = React.useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const isInView = useInView(ref, {
    once: false,
    amount: 0.8,
    margin: "0px 0px -10% 0px",
  });

  // Pre-process words
  const words = text.split(" ").map((word, index) => ({
    word,
    isHighlighted: highlightWords.some(
      (hw) => word.replace(/[.,!?]$/, "").toLowerCase() === hw.toLowerCase()
    ),
  }));

  // Animation variants
  const containerVariants = {
    hidden: {
      transition: {
        staggerChildren: 0.01,
        staggerDirection: -1,
      },
    },
    visible: {
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1,
        staggerDirection: 1,
        type: "spring",
        bounce: 0.2,
      },
    },
  };

  const createWordVariants = (isHighlighted) => ({
    hidden: {
      opacity: 0.45,
      y: prefersReducedMotion ? 0 : 20,
      scale: 0.95,
      color: textColor,
      transition: {
        opacity: { duration: 0.3, ease: "easeIn" },
        y: { duration: 0.3, ease: "easeIn" },
        scale: { duration: 0.3, ease: "easeIn" },
        color: { duration: 0.3, ease: "easeIn" },
      },
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      color: isHighlighted ? highlightColor : textColor,
      transition: {
        opacity: { duration: 0.5, ease: "easeOut" },
        y: { duration: 0.5, type: "spring", damping: 12, stiffness: 100 },
        scale: { duration: 0.5, type: "spring", damping: 12, stiffness: 100 },
        color: { duration: 0.5, ease: "easeOut" },
      },
    },
  });

  return (
    <motion.section
      ref={ref}
      style={{ backgroundColor: bgColor }}
      className="py-12"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div
        className="max-w-3xl mx-auto px-4"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <motion.p
          className="text-xl md:text-3xl text-center leading-relaxed"
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={containerVariants}
        >
          {words.map(({ word, isHighlighted }, index) => (
            <React.Fragment key={index}>
              <motion.span
                variants={createWordVariants(isHighlighted)}
                whileHover={{
                  scale: isHighlighted ? 1.1 : 1.05,
                  transition: { duration: 0.2 },
                }}
              >
                {word}
              </motion.span>
              {index < words.length - 1 && " "}
            </React.Fragment>
          ))}
        </motion.p>
      </motion.div>
    </motion.section>
  );
};

export default AnimatedTextSection;
